/**
 * ImageResizer - Client-side image resizing utility
 *
 * Resizes large images before upload to reduce server load and handle
 * images larger than the 5MB server limit. Uses Canvas API for resizing.
 */
const ImageResizer = {
  MAX_DIMENSION: 2000,
  JPEG_QUALITY: 0.90,
  MAX_FILE_SIZE: 5 * 1024 * 1024,

  /**
   * Process a file, resizing if necessary.
   * @param {File} file - The file to process
   * @param {Object} callbacks - Optional callbacks for progress reporting
   * @param {Function} callbacks.onStart - Called when resizing starts
   * @param {Function} callbacks.onComplete - Called when resizing completes
   * @return {Promise<File>} The processed file (resized or original)
   */
  async processFile(file, callbacks = {}) {
    const {onStart, onComplete} = callbacks;

    try {
      if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
        return file;
      }

      const img = await this._loadImage(file);
      const needsResize = this._needsResize(img, file);

      if (!needsResize) {
        return file;
      }

      // Notify that processing is starting
      if (onStart) {
        onStart({
          originalSize: file.size,
          originalDimensions: {width: img.width, height: img.height},
          needsResize,
        });
      }

      const hasTransparency = await this._hasTransparency(img, file);

      const result = await this._resizeImage(file, img, hasTransparency);

      // Notify that resizing is complete
      if (onComplete) {
        onComplete({
          originalSize: file.size,
          newSize: result.size,
          originalDimensions: {width: img.width, height: img.height},
        });
      }

      return result;
    } catch (error) {
      console.warn('ImageResizer: Error processing file, using original:', error);
      if (onComplete) {
        onComplete({skipped: true, error});
      }
      return file;
    }
  },

  _loadImage(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image'));
      };
      img.src = URL.createObjectURL(file);
    });
  },

  _needsResize(img, file) {
    const exceedsDimensions =
        img.width > this.MAX_DIMENSION || img.height > this.MAX_DIMENSION;
    const exceedsFileSize = file.size > this.MAX_FILE_SIZE;
    return exceedsDimensions || exceedsFileSize;
  },

  async _hasTransparency(img, file) {
    if (file.type !== 'image/png') {
      return false;
    }

    try {
      const canvas = document.createElement('canvas');
      const sampleSize = Math.min(100, img.width, img.height);
      canvas.width = sampleSize;
      canvas.height = sampleSize;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

      const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
      const data = imageData.data;

      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  },

  async _resizeImage(file, img, hasTransparency) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const {width, height} =
        this._calculateDimensions(img.width, img.height);

    canvas.width = width;
    canvas.height = height;

    ctx.drawImage(img, 0, 0, width, height);

    const outputType = hasTransparency ? 'image/png' : 'image/jpeg';
    const quality = hasTransparency ? undefined : this.JPEG_QUALITY;

    const blob = await this._canvasToBlob(canvas, outputType, quality);

    const extension = hasTransparency ? '.png' : '.jpg';
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    const newFileName = baseName + extension;

    return new File([blob], newFileName, {type: outputType});
  },

  _calculateDimensions(width, height) {
    if (width <= this.MAX_DIMENSION && height <= this.MAX_DIMENSION) {
      return {width, height};
    }

    const ratio =
        Math.min(this.MAX_DIMENSION / width, this.MAX_DIMENSION / height);
    return {
      width: Math.round(width * ratio),
      height: Math.round(height * ratio),
    };
  },

  _canvasToBlob(canvas, type, quality) {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), type, quality);
    });
  },
};

export default ImageResizer;
